/**
 * PDF extraction utilities
 *
 * Note: Actual PDF extraction is now done server-side using PyMuPDF
 * for reliability with encrypted/restricted PDFs (like Acrobat Sign).
 * These utilities handle page range parsing and filename generation.
 */

/**
 * Parses a page range string into an array of page numbers.
 * Supports formats like "1-3, 5, 7-9"
 *
 * @param rangeString - The range string to parse
 * @param maxPage - Maximum valid page number
 * @returns Array of page numbers (1-indexed)
 */
export const parsePageRange = (
  rangeString: string,
  maxPage: number,
): number[] => {
  const pages = new Set<number>();
  const parts = rangeString.split(',').map((s) => s.trim());

  parts.forEach((part) => {
    if (!part) return;

    const rangeMatch = part.match(/^(\d+)\s*-\s*(\d+)$/);
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      const min = Math.min(start, end);
      const max = Math.max(start, end);
      Array.from({ length: max - min + 1 }, (_, idx) => min + idx).forEach(
        (i) => {
          if (i >= 1 && i <= maxPage) {
            pages.add(i);
          }
        },
      );
    } else {
      const num = parseInt(part, 10);
      if (!Number.isNaN(num) && num >= 1 && num <= maxPage) {
        pages.add(num);
      }
    }
  });

  return Array.from(pages).sort((a, b) => a - b);
};

/**
 * Formats selected pages into a readable string.
 *
 * @param pages - Set of selected page numbers
 * @returns Formatted string like "1, 3-5, 7"
 */
export const formatPageSelection = (pages: Set<number>): string => {
  const sorted = Array.from(pages).sort((a, b) => a - b);
  if (sorted.length === 0) return '';

  const ranges: string[] = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];

  sorted.slice(1).forEach((page, idx) => {
    if (page === rangeEnd + 1) {
      rangeEnd = page;
    } else {
      if (rangeStart === rangeEnd) {
        ranges.push(String(rangeStart));
      } else if (rangeEnd === rangeStart + 1) {
        ranges.push(`${rangeStart}, ${rangeEnd}`);
      } else {
        ranges.push(`${rangeStart}-${rangeEnd}`);
      }
      rangeStart = page;
      rangeEnd = page;
    }

    // Handle the last element
    if (idx === sorted.length - 2) {
      if (rangeStart === rangeEnd) {
        ranges.push(String(rangeStart));
      } else if (rangeEnd === rangeStart + 1) {
        ranges.push(`${rangeStart}, ${rangeEnd}`);
      } else {
        ranges.push(`${rangeStart}-${rangeEnd}`);
      }
    }
  });

  // Handle single-element case
  if (sorted.length === 1) {
    ranges.push(String(sorted[0]));
  }

  return ranges.join(', ');
};

/**
 * Generates a default filename for the extracted PDF.
 *
 * @param sourceName - Original source filename
 * @param pageCount - Number of pages extracted
 * @returns Suggested filename
 */
export const generateExtractedFilename = (
  sourceName: string,
  pageCount: number,
): string => {
  const baseName = sourceName.replace(/\.pdf$/i, '');
  return `${baseName}_extracted_${pageCount}pg.pdf`;
};
